/**
 * "Back to app" on a standalone product page (one framed by
 * StandalonePageHeader rather than the app shell, release notes today).
 *
 * It steps back through the browser history to the app page the visit started
 * from, rather than opening that page again. Opening it again would add a
 * history entry, and a page whose Close is history.goBack() (Profile, the
 * settings pages and others) would then close straight back into release
 * notes. Stepping back leaves the history as it was before the visit, so every
 * such Close keeps working without this module knowing which pages they are.
 *
 * A page opened from a standalone page (say Profile, from the release-notes
 * header) is part of the visit: Back to app goes to the page before it.
 *
 * To know where that is, the history stack is mirrored here by each entry's
 * key, and kept in sessionStorage (one per tab) so a reload keeps it. A visit
 * with no app page behind it (a direct link, a new tab) opens the last app
 * page remembered, else home.
 */

// Paths rendered outside the app shell.
export const STANDALONE_PATH_PREFIXES = ['/release-notes'];

const HOME_PATH = '/';
const STORAGE_KEY = 'appReturnPath';
// The browser's first entry may carry no key.
const FIRST_KEY = 'first';

export const isStandalonePath = pathname => STANDALONE_PATH_PREFIXES.some(prefix => (
  pathname === prefix || pathname.indexOf(`${prefix}/`) === 0
));

let entries = [];
let index = -1;
let lastAppPath = HOME_PATH;

const keyOf = location => location.key || FIRST_KEY;
const pathOf = location => `${location.pathname}${location.search || ''}`;

const load = () => {
  try {
    const stored = JSON.parse(window.sessionStorage.getItem(STORAGE_KEY));
    if (stored && Array.isArray(stored.entries)) return stored;
  } catch (error) {
    // Storage blocked or unreadable: start afresh.
  }
  return null;
};

const save = () => {
  try {
    window.sessionStorage.setItem(STORAGE_KEY, JSON.stringify({ entries, index, lastAppPath }));
  } catch (error) {
    // Storage blocked or full: a reload falls back to the last app page.
  }
};

/** An entry for `location`: in the visit when standalone, or opened from a standalone page. */
const entryFor = (location, openedFrom) => ({
  key: keyOf(location),
  path: pathOf(location),
  inVisit: isStandalonePath(location.pathname) || !!(openedFrom && openedFrom.standalone),
  standalone: isStandalonePath(location.pathname)
});

const remember = (entry) => {
  if (!entry.inVisit) lastAppPath = entry.path;
};

/** Follow one history change. */
export const rememberLocation = (location, action) => {
  if (!location || !location.pathname) return;
  const current = entries[index];

  if (action === 'POP') {
    const found = entries.findIndex(entry => entry.key === keyOf(location));
    if (found !== -1) {
      index = found;
      remember(entries[index]);
      save();
      return;
    }
  }

  if (action === 'REPLACE' && current) {
    // A redirect keeps where the page was opened from.
    const replaced = entryFor(location, null);
    entries[index] = { ...replaced, inVisit: replaced.inVisit || current.inVisit };
  } else if (action === 'PUSH' && current) {
    entries = [...entries.slice(0, index + 1), entryFor(location, current)];
    index += 1;
  } else {
    // The first page, or one this mirror never saw.
    entries = [entryFor(location, null)];
    index = 0;
  }
  remember(entries[index]);
  save();
};

/**
 * How far back the app page the visit started from is (a negative number for
 * history.go), or null when the visit has none behind it.
 */
export const stepsBackToApp = () => {
  let at = index;
  while (at >= 0 && entries[at].inVisit) at -= 1;
  return at >= 0 && at < index ? at - index : null;
};

/** Where Back to app links to: the last app page remembered, else home. */
export const getAppReturnPath = () => lastAppPath;

/** Back to app: step back to where the visit started, or open the last app page. */
export const goBackToApp = (history) => {
  const steps = stepsBackToApp();
  if (steps === null) history.push(lastAppPath);
  else history.go(steps);
};

/** Start following `history`; returns the unlisten function. */
export const trackAppReturnPath = (history) => {
  const stored = load();
  entries = [];
  index = -1;
  lastAppPath = HOME_PATH;
  if (stored) {
    // After a reload the browser keeps each entry's key, so the stored mirror
    // still matches it; anything else starts afresh below.
    const found = stored.entries.findIndex(entry => entry.key === keyOf(history.location));
    if (found !== -1) {
      ({ entries } = stored);
      index = found;
      lastAppPath = stored.lastAppPath || HOME_PATH;
    }
  }
  rememberLocation(history.location, index === -1 ? null : 'POP');
  return history.listen(rememberLocation);
};
